export const FIX_THICKNESS = 18;

const rowSelectedBg = "rgba(13,110,253,0.12)";
const rowActiveBg = "rgba(13,110,253,0.18)";
const rowHoverBg = "rgba(13,110,253,0.10)";
const rowHoverSelectedBg = "rgba(13,110,253,0.15)";
const cellActiveBg = "#3d8bfd";

export const THEME = {
  fontSize: 12,
  headerBg: "#f7f7f7",
  headerBorder: "#e5e5e5",
  cellBorder: "#eee",
  focusOutline: "#2b6ff5",
  rowHeight: 30,
  headerHeight: 26,
  checkboxColW: "1.2fr",
  nrColW: "1.2fr",
  colorColW: "6fr",
  marasColW: "6fr",
  materialTypeColW: "3fr",
  nameColW: "6fr",
  lengthColW: "3fr",
  widthColW: "3fr",
  qtyColW: "1.5fr",
  rotableColW: "2fr",
  cantTypeColW: "2fr",
  longCantColW: "4fr",
  shortCantColW: "4fr",
  detailsColW: "6fr",
  deleteColW: "1.5rem",
  padHeader: "4px 6px",
  padCell: "0 6px",
  toolbarBg: "#ffffff",
  toolbarBorder: "#e5e5e5",
  radius: 10,
  colors: {
    rowSelectedBg,
    rowActiveBg,
    rowHoverBg,
    rowHoverSelectedBg,
    cellActiveBg
  }
};

export const GRID_TEMPLATE = `${THEME.checkboxColW} ${THEME.nrColW} ${THEME.colorColW} ${THEME.marasColW} ${THEME.materialTypeColW} ${THEME.nameColW} ${THEME.lengthColW} ${THEME.widthColW} ${THEME.qtyColW} ${THEME.rotableColW} ${THEME.cantTypeColW} ${THEME.longCantColW} ${THEME.shortCantColW} ${THEME.detailsColW} ${THEME.deleteColW}`;

export const toolbarStyle = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  border: `1px solid ${THEME.toolbarBorder}`,
  padding: "6px 10px",
  borderRadius: THEME.radius,
  background: THEME.toolbarBg,
  flexWrap: "nowrap",
  minWidth: 0,
  overflow: "hidden"
};

export const COL = {
  NAME: 0,
  H: 1,
  W: 2,
  QTY: 3,
  ROTABLE: 4,
  CANT_TYPE: 5,
  LONG_CANT: 6,
  SHORT_CANT: 7,
  DETAILS: 8
};

export const COLS = 9;

export function btnStyle(variant = "ghost") {
  const base = {
    border: "1px solid transparent",
    cursor: "pointer",
    fontSize: 13,
    borderRadius: 999,
    padding: "6px 12px",
    transition: "transform .06s ease"
  };
  const map = {
    primary: { background: "#0d6efd", color: "#fff" },
    danger: { background: "#ef4444", color: "#fff" },
    ghost: {
      background: "#f3f4f6",
      color: "#111",
      border: "1px solid #e5e7eb"
    },
    muted: {
      background: "#eee",
      color: "#999",
      border: "1px solid #ddd",
      cursor: "not-allowed"
    }
  };
  return { ...base, ...(map[variant] || map.ghost) };
}

export function computeCellBg({
  activeCell,
  rowId,
  colIdx,
  selected,
  isHover
}) {
  const isRowActive = activeCell?.rowId === rowId;
  if (isRowActive) return THEME.colors.rowActiveBg;
  if (selected && isHover) return THEME.colors.rowHoverSelectedBg;
  if (selected) return THEME.colors.rowSelectedBg;
  if (isHover) return THEME.colors.rowHoverBg;
  return "white";
}

export function makeInputCellStyle({ bg, active, align = "left" }) {
  return {
    ...inpStyle,
    background: bg,
    borderRight: `1px solid ${THEME.cellBorder}`,
    borderLeft: `1px solid ${THEME.cellBorder}`,
    outline: "none",
    fontSize: THEME.fontSize,
    height: THEME.rowHeight,
    lineHeight: `${THEME.rowHeight - 4}px`,
    padding: THEME.padCell,
    margin: 0,
    textAlign: align
  };
}

export function makeIconDeleteButtonStyle() {
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    width: 24,
    height: THEME.rowHeight - 8,
    background: "none",
    border: "none",
    color: "#ef4444",
    cursor: "pointer"
  };
}

export const cantTypeOptions = ["-", "04", 2, 1, 42];

export function parseSize(sizeStr) {
  try {
    const [height = 0, width = 0, thickness = 0] = JSON.parse(
      sizeStr || "[0,0,0]"
    );
    return [Number(height) || 0, Number(width) || 0, Number(thickness) || 0];
  } catch {
    return [0, 0, 0];
  }
}

export function buildSizeString(height, width, thickness) {
  return JSON.stringify([
    Number(height) || 0,
    Number(width) || 0,
    Number(thickness) || 0
  ]);
}

export function parseKant(kant) {
  if (Array.isArray(kant)) {
    const [code = "-", longCount = 0, shortCount = 0] = kant;
    return [String(code), Number(longCount) || 0, Number(shortCount) || 0];
  }
  if (typeof kant === "string") {
    const parts = kant
      .replace(/^\[|\]$/g, "")
      .split(",")
      .map((part) => part.trim());
    const [code = "-", longCount = "0", shortCount = "0"] = parts;
    return [String(code), Number(longCount) || 0, Number(shortCount) || 0];
  }
  return ["-", 0, 0];
}

export function buildKantString(code, longCount, shortCount) {
  return `[${code ?? "-"},${Number(longCount) || 0},${Number(shortCount) || 0}]`;
}

export function normalizeCantTypeInput(value) {
  if (value === "04" || value === "-") return value;
  const number = Number(value);
  return Number.isNaN(number) ? value : number;
}

export function buildItemUpdatePayload(updated) {
  const [height = 0, width = 0] =
    updated.sizeArr ??
    (typeof updated.size === "string" ? parseSize(updated.size) : [0, 0]);
  const sizeStr = buildSizeString(height, width, FIX_THICKNESS);
  const kantStr =
    typeof updated.kant === "string"
      ? updated.kant
      : buildKantString(
          updated.cantType ?? "-",
          updated.longCant ?? 0,
          updated.shortCant ?? 0
        );
  const payload = {
    name: updated.name ?? "",
    qty: Number(updated.qty) || 0,
    size: sizeStr,
    kant: kantStr,
    details: updated.details ?? "",
    material: updated.material ?? "",
    rotable: updated.rotable ?? true,
    color:
      updated.colorId == null ? null : { colorId: Number(updated.colorId) },
    object: updated.object?.objectId
      ? { objectId: Number(updated.object.objectId) }
      : undefined,
    work: updated.work?.workId
      ? { workId: Number(updated.work.workId) }
      : undefined,
    position: updated.position ?? "",
    rotation: updated.rotation ?? "",
    tablePosition: updated.tablePosition ?? "",
    tableRotation: updated.tableRotation ?? "",
    millingVariantId: updated.millingId != null ? (updated.millingVariantId || "") : "",
    milling:
      updated.millingId != null
        ? { millingId: Number(updated.millingId) }
        : { millingId: null }
  };
  delete payload.colorId;
  delete payload.sizeArr;
  delete payload.cantType;
  delete payload.longCant;
  delete payload.shortCant;
  delete payload.__idx;
  delete payload.tmpId;
  return payload;
}

function normalizeForCompare(item) {
  const base = buildItemUpdatePayload(item);
  return {
    itemId: item.itemId,
    ...base,
    tablePosition: item.tablePosition ?? ""
  };
}

export function diffChangedItems(original, current) {
  const byId = new Map(original.map((item) => [item.itemId, item]));
  const changed = [];
  for (const item of current) {
    const previous = byId.get(item.itemId);
    if (!previous) {
      changed.push({ ...item, __isNew: true });
      continue;
    }
    if (
      JSON.stringify(normalizeForCompare(previous)) !==
      JSON.stringify(normalizeForCompare(item))
    ) {
      changed.push({ ...item });
    }
  }
  return changed;
}

export const inpStyle = {
  width: "100%",
  padding: "6px",
  height: "2rem",
  border: "none",
  fontSize: 13
};

export const pillSelectStyle = {
  ...inpStyle,
  width: 240,
  background: "#f9fafb",
  border: "1px solid #e5e7eb",
  borderRadius: 999,
  padding: "6px 12px",
  fontSize: 13
};
